/**
 * DealsUpload — the upload landing screen.
 *
 * Replaces the column-mapping wizard as the front door. Brokers no longer map
 * their own spreadsheet layout onto our fields; instead they download OUR
 * template — generated server-side from the app's field metadata and pick
 * lists, so its headers and dropdowns can never drift from the app — fill it
 * in, and upload it back.
 *
 * Two paths, per the upload spec:
 *
 *   NEW      — download the blank multi-tab template, fill it, upload. The
 *              workbook encodes the Deal > Lease > Lease Tract and
 *              Deal > Well > Well Tract hierarchies via temp ref columns.
 *   EXISTING — export the deals you already have into the same shape, edit in
 *              place, upload back. Rows carry real keys, so there is nothing
 *              to match by hand.
 *
 * NEW is live end to end: download, fill, Validate (dry run), Import. EXISTING
 * is not built yet — that card says so rather than presenting a button that
 * does nothing.
 *
 * This is now the ONLY intake path. The column-mapping wizard it replaced (page,
 * route and service calls) was removed outright rather than kept as a fallback:
 * two live importers with different validation would produce two different
 * notions of a valid deal. Its de-hub endpoints still exist but are unreachable
 * from the app.
 *
 * Gated to `can("deal.create")` — UI/UX only; de-hub is the real boundary.
 */
import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Result,
  Row,
  Space,
  Typography,
  Upload,
  message,
} from "antd";
import {
  CheckCircleOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  InboxOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router";

import SignIn from "./signIn";
import { AppContext } from "../AppContextProvider";
import usePermissions from "../hooks/usePermissions";
import dealsService from "../services/dealsService";

const { Title, Paragraph, Text } = Typography;

/**
 * "DEAL: 3 · LEASE: 7" from a `{tab: count}` map, or "" when there is nothing
 * to say. Returning an empty string rather than an empty element lets callers
 * decide whether the whole line should render at all — an update import has no
 * "Added" line, and a fresh import has no "Updated" one.
 */
const countsLine = (counts) =>
  Object.entries(counts || {})
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}: ${v}`)
    .join("  ·  ");
const { Dragger } = Upload;

// Pull a human message out of an axios error envelope. Blob-typed responses
// have already been re-parsed by dealsService.parseBlobError by the time we
// get here, so `detail` reads the same as on any other call.
const errText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") return detail.message || fallback;
  return err?.response?.data?.message || err?.message || fallback;
};

const DealsUpload = () => {
  const navigate = useNavigate();
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);
  const { can } = usePermissions();
  const allowed = can("deal.create");

  const [downloading, setDownloading] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  // New-record upload: pick a file -> validate (dry run) -> commit.
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [validation, setValidation] = useState(null); // clean dry-run result
  const [rejected, setRejected] = useState(null); // { errors, rows }
  const [committed, setCommitted] = useState(null);

  const resetUpload = () => {
    setFile(null);
    setValidation(null);
    setRejected(null);
    setCommitted(null);
  };

  // Defense-in-depth for the UI; the API enforces the real boundary.
  useEffect(() => {
    if (!allowed) navigate("/", { replace: true });
  }, [allowed, navigate]);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const handleDownloadTemplate = async () => {
    try {
      setDownloading(true);
      setErrMsg(null);
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.downloadUploadTemplate({
        token: tk,
        userOid,
      });
      const name = dealsService.saveBlobResponse(
        resp,
        "BDA_New_Record_Upload_Template.xlsx"
      );
      message.success(`Downloaded ${name}`);
    } catch (rawErr) {
      const err = await dealsService.parseBlobError(rawErr);
      console.error("Template download failed:", err);
      const m = errText(err, "Could not download the template.");
      setErrMsg(m);
      message.error(m);
    } finally {
      setDownloading(false);
    }
  };

  /**
   * Run the workbook through the loader.
   *
   * The endpoint answers in one of two shapes and axios must choose
   * `responseType` up front, so the request always asks for a blob: a clean run
   * returns JSON (read back via readBlobJson), a rejected one returns the user's
   * own workbook annotated with an ERROR column (422), which we save straight to
   * their machine — that file IS the error report, so there is nothing to
   * render row-by-row here.
   */
  const runUpload = async (isDryRun) => {
    if (!file) return;
    try {
      setBusy(true);
      setErrMsg(null);
      setRejected(null);
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.uploadTemplate({
        token: tk,
        userOid,
        file,
        dryRun: isDryRun,
      });
      const body = await dealsService.readBlobJson(resp);
      const data = body?.data ?? body ?? {};
      if (isDryRun) {
        setValidation(data);
        message.success("Workbook is valid.");
      } else {
        setCommitted(data);
        setValidation(null);
        message.success("Import complete.");
      }
    } catch (rawErr) {
      if (dealsService.isSpreadsheetResponse(rawErr)) {
        const headers = rawErr?.response?.headers || {};
        const errors = headers["x-upload-errors"];
        const rows = headers["x-upload-error-rows"];
        dealsService.saveBlobResponse(rawErr.response, "upload_errors.xlsx");
        setValidation(null);
        setRejected({ errors, rows });
        message.warning("Nothing was imported — see the downloaded file.");
      } else {
        const err = await dealsService.parseBlobError(rawErr);
        console.error("Template upload failed:", err);
        const m = errText(err, "Upload failed.");
        setErrMsg(m);
        message.error(m);
      }
    } finally {
      setBusy(false);
    }
  };

  const renderUploadArea = () => {
    if (committed) {
      const added = countsLine(committed.created);
      const changed = countsLine(committed.updated);
      const untouched = countsLine(committed.unchanged);
      return (
        <Result
          status="success"
          icon={<CheckCircleOutlined />}
          title={
            committed.mode === "update" ? "Update complete" : "Import complete"
          }
          subTitle={
            <Space direction="vertical" size={4}>
              {changed && (
                <span data-testid="result-updated">
                  <Text strong>Updated:</Text> {changed}
                </span>
              )}
              {added && (
                <span data-testid="result-created">
                  <Text strong>Added:</Text> {added}
                </span>
              )}
              {/* Rows that matched a record but moved nothing. Shown rather
                  than hidden so re-uploading an unedited export reads as
                  "nothing to do" instead of looking like it silently failed. */}
              {untouched && (
                <span data-testid="result-unchanged">
                  <Text type="secondary">Unchanged: {untouched}</Text>
                </span>
              )}
              {!changed && !added && (
                <span data-testid="result-noop">
                  Nothing to change — every row already matched what&apos;s
                  stored.
                </span>
              )}
            </Space>
          }
          extra={[
            <Link key="deals" to="/">
              <Button type="primary">View deals list</Button>
            </Link>,
            <Button key="again" onClick={resetUpload}>
              Import another file
            </Button>,
          ]}
        />
      );
    }

    return (
      <div style={{ marginTop: 20 }}>
        <Dragger
          name="file"
          accept=".xlsx"
          multiple={false}
          showUploadList={false}
          disabled={busy}
          // Capture the file only; the upload itself is an explicit action so
          // the user validates before anything is written.
          beforeUpload={(f) => {
            if (!/\.xlsx$/i.test(f.name)) {
              message.error("Upload the .xlsx template.");
              return Upload.LIST_IGNORE;
            }
            resetUpload();
            setFile(f);
            return false;
          }}
          data-testid="template-dragger"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            {file ? file.name : "Click or drag your filled template here"}
          </p>
          <p className="ant-upload-hint">Single .xlsx file.</p>
        </Dragger>

        {rejected && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            data-testid="upload-rejected"
            message="Nothing was imported"
            description={
              <span>
                {rejected.errors
                  ? `${rejected.errors} problem(s) on ${rejected.rows} row(s). `
                  : ""}
                We&apos;ve downloaded your workbook with an ERROR column on each
                tab. Fix the flagged rows and upload the whole file again.
              </span>
            }
          />
        )}

        {validation && (
          <Alert
            type="success"
            showIcon
            style={{ marginTop: 12 }}
            data-testid="upload-valid"
            message={
              validation.mode === "update"
                ? "Ready to update existing records"
                : "Ready to import"
            }
            description={
              <Space direction="vertical" size={4}>
                {/* Creates and updates are listed SEPARATELY and updates are
                    named first. "40 rows" must never be able to read as 40 new
                    records when it means 40 overwrites — that is the one
                    mistake this screen cannot let a user make. */}
                {countsLine(validation.would_update) && (
                  <span data-testid="would-update">
                    <Text strong>Will update:</Text>{" "}
                    {countsLine(validation.would_update)}
                  </span>
                )}
                {countsLine(validation.would_create) && (
                  <span data-testid="would-create">
                    <Text strong>Will add:</Text>{" "}
                    {countsLine(validation.would_create)}
                  </span>
                )}
                {(validation.new_entities || []).length > 0 && (
                  <span>
                    New parties to be created:{" "}
                    {validation.new_entities.join(", ")}
                  </span>
                )}
              </Space>
            }
          />
        )}

        <Space style={{ marginTop: 12 }}>
          <Button
            onClick={() => runUpload(true)}
            disabled={!file || busy}
            loading={busy && !validation}
            data-testid="validate-template"
          >
            Validate
          </Button>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            onClick={() => runUpload(false)}
            disabled={!validation || busy}
            data-testid="commit-template"
          >
            Import
          </Button>
          {file && (
            <Button type="text" onClick={resetUpload} disabled={busy}>
              Clear
            </Button>
          )}
        </Space>
      </div>
    );
  };

  const renderNewCard = () => (
    <Card
      data-testid="new-records-card"
      title={
        <Space>
          <FileExcelOutlined />
          <span>New records</span>
        </Space>
      }
      style={{ height: "100%" }}
    >
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Download the template, fill in one tab per level, and upload it back.
        The workbook already knows our field names and valid values — every
        constrained field is a dropdown, so there is nothing to map.
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        <Text strong>Deal Key</Text> ties every tab together.{" "}
        <Text strong>Lease Ref</Text> and <Text strong>Tract Ref</Text> are your
        own numbering — 1, 2, 3 — used only to connect rows within the file.
      </Paragraph>

      <Button
        type="primary"
        icon={<DownloadOutlined />}
        loading={downloading}
        onClick={handleDownloadTemplate}
        data-testid="download-template"
      >
        Download template
      </Button>

      {renderUploadArea()}
    </Card>
  );

  const renderExistingCard = () => (
    <Card
      data-testid="existing-records-card"
      title={
        <Space>
          <ExportOutlined />
          <span>Update existing records</span>
        </Space>
      }
      style={{ height: "100%" }}
    >
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Export the deals you already have into the same workbook shape, edit
        them in place, and upload the file back. Because the exported rows carry
        their real keys, each row updates the record it came from — there is no
        matching to get wrong.
      </Paragraph>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Don&apos;t edit the key columns. Any row whose keys were changed is
        rejected rather than repointed at a different record.
      </Paragraph>

      {/* Deliberately NOT a second dropzone. Both kinds of workbook go to the
          same endpoint, which tells them apart by the stamp the exporter
          writes onto INSTRUCTIONS — so a second uploader here would be the
          same control wired to the same call, with two lots of state to keep
          in step and no way for a user to know which one they should have
          used. One uploader, and this card says how to get a file for it. */}
      <Paragraph style={{ marginBottom: 16 }}>
        <Text strong>1.</Text> Pick the deals on the{" "}
        <Link to="/deals">Deals page</Link> and choose{" "}
        <Text strong>Export to template</Text>.
        <br />
        <Text strong>2.</Text> Edit the workbook.
        <br />
        <Text strong>3.</Text> Drop it into the uploader on the left — the same
        one. It recognises an exported file and updates the records it names.
      </Paragraph>

      <Alert
        type="info"
        showIcon
        message="Validate first"
        description={
          "Validate shows how many records will be UPDATED and how many added, "
          + "separately, before anything is written. A row you didn't change "
          + "is left alone rather than rewritten."
        }
      />
    </Card>
  );

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <div style={{ padding: 16, width: "100%", boxSizing: "border-box" }}>
          {allowed ? (
            <>
              <Title level={3} style={{ marginBottom: 4 }}>
                Upload deals
              </Title>
              <Paragraph type="secondary">
                Two ways in: add records you haven&apos;t sent us yet, or update
                ones we already have.
              </Paragraph>

              {errMsg && (
                <Alert
                  type="error"
                  showIcon
                  closable
                  style={{ marginBottom: 16 }}
                  message={errMsg}
                  onClose={() => setErrMsg(null)}
                />
              )}

              <Row gutter={[16, 16]} align="stretch">
                <Col xs={24} lg={12}>
                  {renderNewCard()}
                </Col>
                <Col xs={24} lg={12}>
                  {renderExistingCard()}
                </Col>
              </Row>

            </>
          ) : (
            <Empty description="You don't have permission to import deals." />
          )}
        </div>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default DealsUpload;
